// src/pages/founder/AgentAdoption.jsx
// Level 2: Agent Adoption Detail Page - Light Theme

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  MessageSquare,
  Calendar,
  FileUp,
  FileText,
  Shield,
  ChevronRight,
  Search,
  Download,
  Phone,
  UserPlus,
  Heart
} from "lucide-react";
import { getPilotAdoption, getPilotAdoptionDetail } from "../../api";

// Match the funnel order from FounderCommandCenter
const MILESTONES = [
  { key: 'signedUp', apiKey: 'signedUp', label: 'Registered', icon: UserPlus, color: 'blue' },
  { key: 'sms', apiKey: 'smsConnected', label: 'Phone Setup / CRM Linked', icon: Phone, color: 'cyan' },
  { key: 'calendar', apiKey: 'calendarConnected', label: 'Calendar Linked', icon: Calendar, color: 'teal' },
  { key: 'email', apiKey: 'emailConnected', label: 'Email Linked', icon: Mail, color: 'emerald' },
  { key: 'import', apiKey: 'importedClients', label: 'Clients Uploaded', icon: FileUp, color: 'green' },
  { key: 'bluebutton', apiKey: 'bluebuttonConnected', label: 'Blue Button Connected', icon: Heart, color: 'rose' }
];

export default function AgentAdoption() {
  const navigate = useNavigate();
  const { milestone: urlMilestone } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(urlMilestone || null);
  const [milestoneDetail, setMilestoneDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedMilestone && selectedMilestone !== 'signedUp') {
      loadMilestoneDetail(selectedMilestone);
    } else {
      setMilestoneDetail(null);
    }
  }, [selectedMilestone]);

  const loadData = async () => {
    try {
      const res = await getPilotAdoption();
      setData(res?.data);
    } catch (err) {
      console.error('Failed to load adoption data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMilestoneDetail = async (milestone) => {
    setDetailLoading(true);
    try {
      const res = await getPilotAdoptionDetail(milestone);
      setMilestoneDetail(res?.data);
    } catch (err) {
      console.error('Failed to load milestone detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredAgents = data?.agents?.filter(agent =>
    agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/founder')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Command Center
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Agent Onboarding</h1>
              <p className="text-slate-500">Track onboarding progress and feature adoption</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Funnel Overview */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-6">Onboarding Funnel</h2>
          <div className="space-y-3">
            {MILESTONES.map((milestone, idx) => {
              const stepData = data?.funnel?.[milestone.apiKey] || { count: 0, percent: 0 };
              const isSelected = selectedMilestone === milestone.key;

              return (
                <FunnelStep
                  key={milestone.key}
                  label={milestone.label}
                  icon={milestone.icon}
                  count={stepData.count}
                  percent={stepData.percent}
                  total={data?.funnel?.signedUp?.count || 1}
                  index={idx}
                  onClick={() => setSelectedMilestone(milestone.key)}
                  selected={isSelected}
                />
              );
            })}
          </div>
        </div>

        {/* Milestone Detail Panel */}
        {selectedMilestone && selectedMilestone !== 'signedUp' && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <h3 className="text-lg font-semibold text-slate-900">
                {MILESTONES.find(m => m.key === selectedMilestone)?.label} - Details
              </h3>
              <button
                onClick={() => setSelectedMilestone(null)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            {detailLoading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : milestoneDetail && (
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Completed */}
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2 bg-emerald-50 px-3 py-2 rounded-lg">
                      <CheckCircle2 className="w-4 h-4" />
                      Completed ({milestoneDetail.completed?.count || 0})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {milestoneDetail.completed?.agents?.map(agent => (
                        <div key={agent.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between hover:border-slate-300 transition-colors">
                          <div>
                            <div className="text-slate-900 font-medium">{agent.name}</div>
                            <div className="text-xs text-slate-500">{agent.email}</div>
                          </div>
                          <div className="text-xs text-slate-500">
                            {agent.completed_at && new Date(agent.completed_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                      {(!milestoneDetail.completed?.agents || milestoneDetail.completed.agents.length === 0) && (
                        <div className="text-sm text-slate-500 text-center py-4">No agents completed this step yet</div>
                      )}
                    </div>
                  </div>
                  {/* Not Completed */}
                  <div>
                    <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2 bg-amber-50 px-3 py-2 rounded-lg">
                      <Clock className="w-4 h-4" />
                      Not Yet ({milestoneDetail.notCompleted?.count || 0})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {milestoneDetail.notCompleted?.agents?.map(agent => (
                        <div key={agent.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between hover:border-slate-300 transition-colors">
                          <div>
                            <div className="text-slate-900 font-medium">{agent.name}</div>
                            <div className="text-xs text-slate-500">{agent.email}</div>
                          </div>
                          <div className="text-xs text-slate-500">
                            Last login: {agent.last_login_at ? new Date(agent.last_login_at).toLocaleDateString() : 'Never'}
                          </div>
                        </div>
                      ))}
                      {(!milestoneDetail.notCompleted?.agents || milestoneDetail.notCompleted.agents.length === 0) && (
                        <div className="text-sm text-slate-500 text-center py-4">All agents completed this step!</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Agent List */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900">All Agents ({filteredAgents.length})</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
              <button className="p-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors">
                <Download className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Agent</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Clients</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Progress</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Milestones</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAgents.map(agent => (
                  <tr key={agent.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {agent.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="text-slate-900 font-medium">{agent.name}</div>
                          <div className="text-xs text-slate-500">{agent.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-slate-900 font-semibold">{agent.clientCount}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              (agent.completedSteps / 6) >= 0.7 ? 'bg-emerald-500' :
                              (agent.completedSteps / 6) >= 0.4 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${(agent.completedSteps / 6) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{agent.completedSteps}/6</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {MILESTONES.slice(1).map(m => { // Skip "Registered" since everyone has it
                          const key = m.key === 'pdf-parser' ? 'pdfParser' : m.key;
                          const completed = agent.milestones?.[key];
                          return (
                            <div
                              key={m.key}
                              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                                completed ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
                              }`}
                              title={`${m.label}: ${completed ? 'Completed' : 'Not completed'}`}
                            >
                              <m.icon className="w-3.5 h-3.5" />
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {agent.lastLoginAt ? formatRelativeTime(agent.lastLoginAt) : 'Never'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAgents.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No agents found</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function FunnelStep({ label, icon: Icon, count, percent, total, index, onClick, selected }) {
  const width = total > 0 ? (count / total) * 100 : 0;
  const colors = [
    'from-blue-500 to-blue-400',
    'from-cyan-500 to-cyan-400',
    'from-teal-500 to-teal-400',
    'from-emerald-500 to-emerald-400',
    'from-green-500 to-green-400',
    'from-rose-500 to-rose-400'
  ];

  return (
    <div
      className={`flex items-center gap-4 p-3 rounded-xl transition-all cursor-pointer ${
        selected
          ? 'bg-amber-50 ring-2 ring-amber-400 shadow-sm'
          : 'hover:bg-slate-50 border border-transparent hover:border-slate-200'
      }`}
      onClick={onClick}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
        selected ? 'bg-amber-100' : 'bg-slate-100'
      }`}>
        <Icon className={`w-5 h-5 ${selected ? 'text-amber-600' : 'text-slate-600'}`} />
      </div>
      <div className="w-40 text-sm font-medium text-slate-700">{label}</div>
      <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colors[index % colors.length]} rounded-lg flex items-center justify-end pr-3 transition-all duration-500`}
          style={{ width: `${Math.max(width, 8)}%` }}
        >
          <span className="text-sm font-bold text-white drop-shadow">{count}</span>
        </div>
      </div>
      <div className="w-16 text-right">
        <span className="text-sm font-semibold text-slate-700">{percent}%</span>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </div>
  );
}

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
